import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Plus, FileText, Users, TrendingUp, AlertCircle, IndianRupee, FilePlus,
    Receipt, Clock, Percent, ArrowRight, Activity, Award, DollarSign,
    Building2, CheckCircle2, ArrowUpRight, FileSpreadsheet, Download, Loader2,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { api, fmtMoney, fmtDate } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

export default function InvoicingDashboard() {
    const [summary, setSummary] = useState(null);
    const [aging, setAging] = useState(null);
    const [recent, setRecent] = useState([]);
    const [trend, setTrend] = useState(null);
    const [top, setTop] = useState([]);
    const [tax, setTax] = useState(null);
    const [velocity, setVelocity] = useState(null);
    const [payments, setPayments] = useState([]);
    const [overdue, setOverdue] = useState([]);
    const [taxPeriod, setTaxPeriod] = useState('mtd');
    const [loading, setLoading] = useState(true);
    const [tallyDialogOpen, setTallyDialogOpen] = useState(false);
    const nav = useNavigate();

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, a, r, t, tc, ts, cv, rp, od] = await Promise.all([
                api.invSummary(),
                api.invAgingReport(),
                api.invListInvoices({ limit: 5 }),
                api.invSalesTrend(6),
                api.invTopCustomers(5),
                api.invTaxSummary(taxPeriod),
                api.invCollectionVelocity(),
                api.invRecentPayments(6),
                api.invTopOverdue(5),
            ]);
            setSummary(s); setAging(a); setRecent(r.items || []);
            setTrend(t); setTop(tc.items || []); setTax(ts);
            setVelocity(cv); setPayments(rp.items || []); setOverdue(od.items || []);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
        // taxPeriod intentionally omitted — first mount uses default; changes
        // are picked up by the second effect below without reloading everything.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => {
        if (!loading) api.invTaxSummary(taxPeriod).then(setTax).catch(() => {});
    }, [taxPeriod, loading]);

    const kpis = [
        { label: 'MTD Revenue', value: fmtMoney(summary?.mtd_issued || 0), sub: `${summary?.mtd_count || 0} invoices issued`, icon: TrendingUp, tone: 'blue', testid: 'kpi-mtd-issued' },
        { label: 'Outstanding', value: fmtMoney(summary?.outstanding || 0), sub: 'Total unpaid balance', icon: IndianRupee, tone: 'amber', testid: 'kpi-outstanding' },
        { label: 'Overdue', value: (summary?.overdue_count || 0).toString(), sub: `${fmtMoney(overdue.reduce((s, x) => s + (x.overdue_amount || 0), 0))} past due`, icon: AlertCircle, tone: (summary?.overdue_count || 0) > 0 ? 'red' : 'muted', testid: 'kpi-overdue' },
        { label: 'Avg. Days to Pay', value: velocity ? `${velocity.average_days}d` : '—', sub: `${velocity?.sample_count || 0} recent payments`, icon: Clock, tone: 'emerald', testid: 'kpi-velocity' },
        { label: 'Drafts', value: (summary?.draft_count || 0).toString(), sub: 'Unissued invoices', icon: FilePlus, tone: 'slate', testid: 'kpi-drafts' },
    ];

    return (
        <div className="space-y-5" data-testid="invoicing-dashboard">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-heading font-semibold tracking-tight">Invoicing</h1>
                    <p className="text-sm text-muted-foreground mt-1">Create GST-compliant invoices, track payments, and manage receivables.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" asChild data-testid="btn-settings"><Link to="/invoicing/settings">Settings</Link></Button>
                    <Button variant="outline" size="sm" asChild data-testid="btn-customers"><Link to="/invoicing/customers"><Users size={14} className="mr-1.5" /> Customers</Link></Button>
                    <Button variant="outline" size="sm" asChild data-testid="btn-view-invoices"><Link to="/invoicing/invoices"><FileText size={14} className="mr-1.5" /> All invoices</Link></Button>
                    <Button variant="outline" size="sm" asChild data-testid="btn-new-customer"><Link to="/invoicing/customers/new"><Users size={14} className="mr-1.5" /> New customer</Link></Button>
                    <Button size="sm" asChild data-testid="btn-new-invoice"><Link to="/invoicing/invoices/new"><Plus size={14} className="mr-1.5" /> New invoice</Link></Button>
                </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {kpis.map((k) => (
                    <Card key={k.label} data-testid={k.testid} className="relative overflow-hidden">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardDescription className="text-xs">{k.label}</CardDescription>
                                <div className={`p-1.5 rounded ${toneBg(k.tone)}`}>
                                    <k.icon size={14} className={toneText(k.tone)} />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loading ? <Skeleton className="h-7 w-20" /> : (
                                <div className="text-xl md:text-2xl font-heading font-semibold tabular-nums">{k.value}</div>
                            )}
                            <div className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{k.sub}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Sprint 2 (2026-07): quick access to Quotes / Credit Notes / Debit Notes.
                Each opens its own list view; all three share the invoice pipeline
                under the hood via the doc_type discriminator. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="doc-types-row">
                <Link
                    to="/invoicing/quotes"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between"
                    data-testid="cta-quotes"
                >
                    <div>
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} className="text-blue-600" /> Quotes / Estimates
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Send a proposal, convert to invoice on approval.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
                <Link
                    to="/invoicing/credit-notes"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between"
                    data-testid="cta-credit-notes"
                >
                    <div>
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} className="text-rose-600" /> Credit Notes
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">GST-compliant refunds &amp; adjustments.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
                <Link
                    to="/invoicing/debit-notes"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between"
                    data-testid="cta-debit-notes"
                >
                    <div>
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} className="text-amber-600" /> Debit Notes
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Charge back for undercharged amounts.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
            </div>

            {/* Sprint 2026-09: broader commercial documents. Delivery Challan,
                Sales Order and Purchase Order — the full paper trail every
                wholesaler expects. Plus the GST Filing helper tile that
                exports GSTR-1 / GSTR-3B ready for the portal. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="doc-types-row-2">
                <Link
                    to="/invoicing/delivery-challans"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between"
                    data-testid="cta-delivery-challans"
                >
                    <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} className="text-teal-600" /> Delivery Challans
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">Dispatch without a bill.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </Link>
                <Link
                    to="/invoicing/sales-orders"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between"
                    data-testid="cta-sales-orders"
                >
                    <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} className="text-indigo-600" /> Sales Orders
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">Confirm before billing.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </Link>
                <Link
                    to="/invoicing/purchase-orders"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between"
                    data-testid="cta-purchase-orders"
                >
                    <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} className="text-slate-600" /> Purchase Orders
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">Order from suppliers.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </Link>
                <Link
                    to="/invoicing/gst-filing"
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between bg-gradient-to-br from-emerald-50 to-transparent dark:from-emerald-900/20"
                    data-testid="cta-gst-filing"
                >
                    <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <Activity size={14} className="text-emerald-600" /> GST Filing
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">GSTR-1 &amp; 3B exports.</div>
                    </div>
                    <Plus size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </Link>
                <button
                    type="button"
                    onClick={() => setTallyDialogOpen(true)}
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex items-center justify-between bg-gradient-to-br from-orange-50 to-transparent dark:from-orange-900/20 text-left"
                    data-testid="cta-tally-export"
                >
                    <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <FileSpreadsheet size={14} className="text-orange-600" /> Export to Tally
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">One-click XML for TallyPrime.</div>
                    </div>
                    <Download size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </button>
            </div>

            {/* Sales trend + Tax summary side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-2" data-testid="sales-trend-card">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-base flex items-center gap-2"><Activity size={16} /> Sales Trend</CardTitle>
                            <CardDescription className="text-xs">Last 6 months — issued (blue) vs collected (green)</CardDescription>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-48 w-full" /> : <SalesTrendChart data={trend?.months || []} />}
                    </CardContent>
                </Card>

                <Card data-testid="tax-summary-card">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2"><Percent size={16} /> Tax Collected</CardTitle>
                            <Tabs value={taxPeriod} onValueChange={setTaxPeriod}>
                                <TabsList className="h-7">
                                    <TabsTrigger value="mtd" className="text-[10px] px-2 h-5" data-testid="tab-mtd">MTD</TabsTrigger>
                                    <TabsTrigger value="fy" className="text-[10px] px-2 h-5" data-testid="tab-fy">FY</TabsTrigger>
                                    <TabsTrigger value="ytd" className="text-[10px] px-2 h-5" data-testid="tab-ytd">YTD</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {loading || !tax ? <Skeleton className="h-32 w-full" /> : (
                            <>
                                <div className="text-2xl font-heading font-semibold tabular-nums text-primary" data-testid="tax-total">{fmtMoney(tax.total_gst)}</div>
                                <div className="text-[11px] text-muted-foreground -mt-2">Across {tax.invoice_count} invoices</div>
                                <div className="space-y-1.5 pt-2">
                                    <TaxRow lbl="CGST" val={tax.cgst} color="bg-blue-500" total={tax.total_gst} />
                                    <TaxRow lbl="SGST" val={tax.sgst} color="bg-indigo-500" total={tax.total_gst} />
                                    <TaxRow lbl="IGST" val={tax.igst} color="bg-emerald-500" total={tax.total_gst} />
                                </div>
                                <div className="pt-2 mt-1 border-t border-border/50 text-xs text-muted-foreground flex items-center justify-between">
                                    <span>Taxable value</span>
                                    <span className="tabular-nums">{fmtMoney(tax.subtotal_taxable)}</span>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Row: Top customers + Top overdue + Aging */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card data-testid="top-customers-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><Award size={16} /> Top Customers</CardTitle>
                        <CardDescription className="text-xs">By total revenue billed</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-32 w-full" /> : top.length === 0 ? (
                            <EmptyBox icon={Users} text="No customer data yet" />
                        ) : (
                            <div className="space-y-2">
                                {top.map((c, i) => (
                                    <Link key={c.customer_id} to={`/invoicing/customers/${c.customer_id}`} data-testid={`top-cust-${i}`} className="flex items-center gap-3 group py-1">
                                        <div className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${rankBadge(i)}`}>{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate group-hover:text-primary">{c.customer_name || '—'}</div>
                                            <div className="text-[10px] text-muted-foreground">{c.invoice_count} invoice{c.invoice_count !== 1 ? 's' : ''}{c.total_due > 0 ? ` · ${fmtMoney(c.total_due)} due` : ''}</div>
                                        </div>
                                        <div className="text-sm font-semibold tabular-nums">{fmtMoney(c.total_billed)}</div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="top-overdue-card" className={overdue.length > 0 ? 'border-red-200/70 dark:border-red-900/40' : ''}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><AlertCircle size={16} className={overdue.length > 0 ? 'text-red-600' : ''} /> Overdue Attention</CardTitle>
                        <CardDescription className="text-xs">Customers with largest overdue balances</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-32 w-full" /> : overdue.length === 0 ? (
                            <EmptyBox icon={CheckCircle2} text="No overdue invoices" iconColor="text-emerald-500" />
                        ) : (
                            <div className="space-y-2">
                                {overdue.map((o, i) => (
                                    <Link key={o.customer_id} to={`/invoicing/customers/${o.customer_id}`} data-testid={`overdue-${i}`} className="flex items-center gap-3 group py-1">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate group-hover:text-primary">{o.customer_name || '—'}</div>
                                            <div className="text-[10px] text-red-600 dark:text-red-400">{o.invoice_count} inv · oldest {o.oldest_days}d late</div>
                                        </div>
                                        <div className="text-sm font-semibold tabular-nums text-red-700 dark:text-red-400">{fmtMoney(o.overdue_amount)}</div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="aging-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><IndianRupee size={16} /> Receivables Aging</CardTitle>
                        <CardDescription className="text-xs">Total unpaid: <span className="font-medium">{fmtMoney(aging?.total_outstanding || 0)}</span></CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-32 w-full" /> : (
                            <div className="space-y-2.5">
                                {['current', '0-30', '30-60', '60-90', '90+'].map((bkt) => {
                                    const amt = aging?.buckets?.[bkt] || 0;
                                    const count = aging?.counts?.[bkt] || 0;
                                    const total = aging?.total_outstanding || 1;
                                    const pct = total ? (amt / total) * 100 : 0;
                                    const isOverdue = bkt !== 'current' && amt > 0;
                                    return (
                                        <div key={bkt} data-testid={`aging-bucket-${bkt}`}>
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-muted-foreground">{bkt === 'current' ? 'Not yet due' : `${bkt}d overdue`}{count ? ` · ${count}` : ''}</span>
                                                <span className="font-medium tabular-nums">{fmtMoney(amt)}</span>
                                            </div>
                                            <div className="h-1.5 rounded bg-muted overflow-hidden">
                                                <div className={`h-full ${isOverdue ? 'bg-red-500' : 'bg-primary'}`} style={{ width: `${Math.max(pct > 0 ? 3 : 0, pct)}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Row: Recent invoices + Recent payments */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card data-testid="recent-invoices-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center justify-between">
                            <span className="flex items-center gap-2"><Receipt size={16} /> Recent Invoices</span>
                            <Button variant="link" size="sm" asChild className="h-auto p-0 text-xs"><Link to="/invoicing/invoices">View all <ArrowRight size={12} className="ml-0.5" /></Link></Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-32 w-full" /> : recent.length === 0 ? (
                            <EmptyBox icon={FileText} text="No invoices yet" cta={{ label: 'Create your first invoice', to: '/invoicing/invoices/new' }} />
                        ) : (
                            <div className="space-y-1">
                                {recent.map((inv) => (
                                    <Link key={inv.id} to={`/invoicing/invoices/${inv.id}`} data-testid={`recent-invoice-${inv.id}`} className="flex items-center justify-between py-2 px-2 -mx-2 rounded hover:bg-muted/60 transition-colors">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-sm truncate">{inv.invoice_number || <span className="italic text-muted-foreground">DRAFT</span>}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">{inv.customer_snapshot?.name || '—'} · {fmtDate(inv.invoice_date)}</div>
                                        </div>
                                        <div className="text-right ml-3">
                                            <div className="font-semibold tabular-nums text-sm">{fmtMoney(inv.grand_total)}</div>
                                            <StatusBadgeSmall status={inv.status} overdue={inv.overdue} />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="recent-payments-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><DollarSign size={16} /> Recent Payments</CardTitle>
                        <CardDescription className="text-xs">Latest cash inflows</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-32 w-full" /> : payments.length === 0 ? (
                            <EmptyBox icon={DollarSign} text="No payments recorded yet" />
                        ) : (
                            <div className="space-y-1">
                                {payments.map((p) => (
                                    <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-center justify-between py-2 px-2 -mx-2 rounded hover:bg-muted/40 transition-colors">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{p.customer_name || 'On account'}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">
                                                {fmtDate(p.payment_date)} · <span className="capitalize">{p.mode}</span>
                                                {p.invoice_number ? ` · ${p.invoice_number}` : ''}
                                            </div>
                                        </div>
                                        <div className="text-right ml-3">
                                            <div className="font-semibold tabular-nums text-sm text-emerald-700 dark:text-emerald-400">+ {fmtMoney(p.amount)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Sprint 2026-09: Tally export dialog. Opens from the
                "Export to Tally" tile. Backend endpoint returns an
                XML blob that the user drops into Tally → Import Data. */}
            <TallyExportDialog open={tallyDialogOpen} onOpenChange={setTallyDialogOpen} />
        </div>
    );
}

// ---------------------------------------------------------------
// TallyExportDialog (Sprint 2026-09)
// ---------------------------------------------------------------
function TallyExportDialog({ open, onOpenChange }) {
    // Default to last completed month — that's the period the user is
    // handing to their CA right now.
    const defaultMonth = (() => {
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() - 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();
    const [month, setMonth] = useState(defaultMonth);
    const [includeSales, setIncludeSales] = useState(true);
    const [includePurchase, setIncludePurchase] = useState(true);
    const [includeReceipts, setIncludeReceipts] = useState(true);
    const [busy, setBusy] = useState(false);

    async function handleExport() {
        try {
            setBusy(true);
            const resp = await api.invTallyExport({
                month,
                include_sales: includeSales,
                include_purchase: includePurchase,
                include_receipts: includeReceipts,
            });
            const blob = resp.data;
            const dispo = resp.headers?.['content-disposition'] || '';
            const m = dispo.match(/filename="([^"]+)"/);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = m?.[1] || `Tally-Import-${month}.xml`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            const salesN = resp.headers?.['x-tally-docs-sales'] || '?';
            const purN = resp.headers?.['x-tally-docs-purchase'] || '?';
            const rcpN = resp.headers?.['x-tally-docs-receipts'] || '?';
            toast.success('Tally XML downloaded', {
                description: `${salesN} sales · ${purN} purchase · ${rcpN} receipts`,
            });
            onOpenChange?.(false);
        } catch (e) {
            toast.error('Export failed', {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="tally-export-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet size={18} className="text-orange-600" />
                        Export to Tally
                    </DialogTitle>
                    <DialogDescription>
                        Downloads a Tally-compatible XML file. In Tally / TallyPrime:
                        <span className="block mt-1 text-foreground/90 text-xs">
                            Gateway → Import Data → Vouchers → point at this file.
                        </span>
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs text-muted-foreground">Return period (month)</Label>
                        <Input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            data-testid="tally-month-picker"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Include</Label>
                        <div className="space-y-2 pt-1">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox checked={includeSales} onCheckedChange={setIncludeSales} data-testid="tally-inc-sales" />
                                <span>Sales vouchers <span className="text-muted-foreground text-xs">(invoices, credit &amp; debit notes)</span></span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox checked={includePurchase} onCheckedChange={setIncludePurchase} data-testid="tally-inc-purchase" />
                                <span>Purchase vouchers <span className="text-muted-foreground text-xs">(scanned bills)</span></span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox checked={includeReceipts} onCheckedChange={setIncludeReceipts} data-testid="tally-inc-receipts" />
                                <span>Receipt vouchers <span className="text-muted-foreground text-xs">(payments received)</span></span>
                            </label>
                        </div>
                    </div>
                    <div className="rounded-md border border-amber-200 bg-amber-50/60 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-100">
                        <strong>Tip:</strong> Ledger masters for your customers and vendors are auto-included so Tally creates any missing ledgers on import — you don't have to set them up first.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)} data-testid="tally-cancel-btn">
                        Cancel
                    </Button>
                    <Button size="sm" onClick={handleExport} disabled={busy} data-testid="tally-download-btn">
                        {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
                        Download XML
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------- helpers ----------------
function toneBg(t) {
    return {
        blue: 'bg-blue-100 dark:bg-blue-950/40', amber: 'bg-amber-100 dark:bg-amber-950/40',
        red: 'bg-red-100 dark:bg-red-950/40', emerald: 'bg-emerald-100 dark:bg-emerald-950/40',
        slate: 'bg-slate-100 dark:bg-slate-800/60', muted: 'bg-muted',
    }[t] || 'bg-muted';
}
function toneText(t) {
    return {
        blue: 'text-blue-600 dark:text-blue-400', amber: 'text-amber-700 dark:text-amber-400',
        red: 'text-red-700 dark:text-red-400', emerald: 'text-emerald-700 dark:text-emerald-400',
        slate: 'text-slate-600 dark:text-slate-400', muted: 'text-muted-foreground',
    }[t] || 'text-muted-foreground';
}
function rankBadge(i) {
    if (i === 0) return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    if (i === 1) return 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    if (i === 2) return 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300';
    return 'bg-muted text-muted-foreground';
}

function EmptyBox({ icon: Icon, text, cta, iconColor = 'text-muted-foreground/40' }) {
    return (
        <div className="text-center py-6 text-sm text-muted-foreground">
            <Icon size={28} className={`mx-auto mb-2 ${iconColor}`} />
            <div>{text}</div>
            {cta && <Link to={cta.to} className="text-primary text-xs font-medium mt-2 inline-flex items-center gap-1">{cta.label} <ArrowUpRight size={12} /></Link>}
        </div>
    );
}

function TaxRow({ lbl, val, color, total }) {
    const pct = total ? (val / total) * 100 : 0;
    return (
        <div>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="text-muted-foreground">{lbl}</span>
                <span className="tabular-nums font-medium">{fmtMoney(val)}</span>
            </div>
            <div className="h-1 rounded bg-muted overflow-hidden">
                <div className={`h-full ${color}`} style={{ width: `${Math.max(pct > 0 ? 3 : 0, pct)}%` }} />
            </div>
        </div>
    );
}

export function StatusBadge({ status, overdue }) {
    return <StatusBadgeSmall status={status} overdue={overdue} />;
}

function StatusBadgeSmall({ status, overdue }) {
    const map = {
        draft: { label: 'Draft', cls: 'bg-muted text-muted-foreground' },
        issued: { label: 'Issued', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
        partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
        paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
        cancelled: { label: 'Cancelled', cls: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 line-through' },
    };
    const m = map[status] || map.draft;
    return (
        <div className="flex items-center gap-1 justify-end mt-0.5">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${m.cls}`}>{m.label}</span>
            {overdue && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">Overdue</span>}
        </div>
    );
}

// Simple SVG-based sales trend bar chart
function SalesTrendChart({ data }) {
    if (!data || data.length === 0) {
        return <div className="text-center py-8 text-sm text-muted-foreground">No sales data yet</div>;
    }
    const maxVal = Math.max(...data.map(d => Math.max(d.issued || 0, d.collected || 0)), 1);
    const w = 640; const h = 200; const pad = { top: 10, right: 10, bottom: 30, left: 50 };
    const chartW = w - pad.left - pad.right;
    const chartH = h - pad.top - pad.bottom;
    const barGroup = chartW / data.length;
    const barW = Math.min(30, barGroup * 0.36);

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[400px] max-w-[720px]">
                {/* Grid lines */}
                {[0.25, 0.5, 0.75, 1].map((r) => (
                    <line key={r} x1={pad.left} x2={w - pad.right} y1={pad.top + chartH * (1 - r)} y2={pad.top + chartH * (1 - r)} stroke="currentColor" strokeOpacity="0.08" />
                ))}
                {[0.25, 0.5, 0.75, 1].map((r) => (
                    <text key={r} x={pad.left - 8} y={pad.top + chartH * (1 - r) + 4} fontSize="9" textAnchor="end" fill="currentColor" opacity="0.5">
                        {fmtCompact(maxVal * r)}
                    </text>
                ))}
                {/* Bars */}
                {data.map((d, i) => {
                    const groupX = pad.left + i * barGroup + barGroup / 2;
                    const issuedH = ((d.issued || 0) / maxVal) * chartH;
                    const collectedH = ((d.collected || 0) / maxVal) * chartH;
                    return (
                        <g key={d.label || `bar-${i}`}>
                            <rect x={groupX - barW - 2} y={pad.top + chartH - issuedH} width={barW} height={issuedH} rx="2" className="fill-blue-500" />
                            <rect x={groupX + 2} y={pad.top + chartH - collectedH} width={barW} height={collectedH} rx="2" className="fill-emerald-500" />
                            <text x={groupX} y={h - 12} fontSize="10" textAnchor="middle" fill="currentColor" opacity="0.7">{d.label}</text>
                            {d.issued > 0 && (
                                <text x={groupX - barW / 2 - 2} y={pad.top + chartH - issuedH - 3} fontSize="8" textAnchor="middle" fill="currentColor" opacity="0.55">{fmtCompact(d.issued)}</text>
                            )}
                        </g>
                    );
                })}
            </svg>
            <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground mt-1">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-blue-500 inline-block" /> Issued</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-emerald-500 inline-block" /> Collected</span>
            </div>
        </div>
    );
}

function fmtCompact(v) {
    v = Number(v || 0);
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return v.toFixed(0);
}
